import { Component, memo, Suspense } from 'react';
import { InfoIcon, MapIcon, MessageIcon, RadarIcon, RefreshIcon } from '../icons';
import { Button } from '../Button';
import { VisualSkeleton } from './VisualSkeleton';
import { retryableLazy } from './retryableLazy.js';
import { ARTIFACT_INTERRUPTED } from '../../hooks/messageStream.js';
import { visualFamily } from './visualTypes.js';
import { DocumentSkeleton } from '../documents/DocumentSkeleton';
import './Visuals.css';
import './map/LiveMapCard.css';

export { VisualAskContext } from './visualContext.js';

// Carte complète (graphique, actions, exports) : chunk séparé, chargé au premier visuel.
// ECharts lui-même est encore à part (chargé seulement pour un graphique).
// Un échec de chargement n'est pas définitif (retryableLazy).
const VisualCard = retryableLazy(() => import('./VisualCard.jsx'));
// Sprint 4 (contrat v1.2) : chaque famille dans son propre chunk ; MapLibre, lui, n'est
// téléchargé qu'à l'affichage de la carte (map/mapLoader.js)
// Carte live (03/11) : une carte de la réponse devient une couche de la carte du panneau de
// droite ; le fil n'en garde qu'une trace compacte (version, ce qui a changé)
const LiveMapCard = retryableLazy(() => import('./map/LiveMapCard.jsx'));
const PlanningCard = retryableLazy(() => import('./planning/PlanningCard.jsx'));
const EmbedCard = retryableLazy(() => import('./embed/EmbedCard.jsx'));
// Sprint 5 (contrat v1.3) : document généré (PDF, Word, Excel, PowerPoint)
const DocumentCard = retryableLazy(() => import('../documents/DocumentCard.jsx'));
// Brouillon d'e-mail (prepare_email) : relu, modifié et envoyé par l'utilisateur ; carte
// d'annulation d'un envoi programmé (prepare_email_cancellation), annulé par son clic
const EmailDraftCard = retryableLazy(() => import('../email/EmailDraftCard.jsx'));
const EmailCancelCard = retryableLazy(() => import('../email/EmailCancelCard.jsx'));
// Contrat v1.13 : galerie de photos d'un véhicule, construite par le serveur (get_photos_vehicule)
const PhotosCard = retryableLazy(() => import('../fichiers/PhotosCard.jsx'));
// P1 (contrat v1.15) : cartes « Nouvel agent » (prepare_agent) et « Modification d'agent »
// (prepare_agent_change) ; l'agent n'est créé ou modifié qu'au clic de l'utilisateur
const AgentDraftCard = retryableLazy(() => import('../agents/AgentDraftCard.jsx'));
const AgentChangeCard = retryableLazy(() => import('../agents/AgentChangeCard.jsx'));
const CARDS = {
  chart: VisualCard,
  map: LiveMapCard,
  map_patch: LiveMapCard,
  planning: PlanningCard,
  embed: EmbedCard,
  document: DocumentCard,
  email: EmailDraftCard,
  email_cancel: EmailCancelCard,
  photos: PhotosCard,
  agent: AgentDraftCard,
  agent_change: AgentChangeCard,
};

const kindOf = (artifact) => artifact.spec?.kind ?? artifact.kind;
const isDocument = (artifact) => kindOf(artifact) === 'document';
const isEmail = (artifact) => kindOf(artifact) === 'email' || kindOf(artifact) === 'email_cancel';
const isAgent = (artifact) => kindOf(artifact) === 'agent' || kindOf(artifact) === 'agent_change';

// Messages neutres d'un document refusé ou interrompu (le contrat parle de « visuel »)
function noticeMessage(artifact) {
  if (isEmail(artifact)) {
    if (artifact.message && artifact.message !== ARTIFACT_INTERRUPTED) return artifact.message;
    return kindOf(artifact) === 'email_cancel' ? "La carte d'annulation n'a pas pu être préparée." : "Le brouillon d'e-mail n'a pas pu être préparé.";
  }
  // Message du serveur (« Agent non préparé : … ») gardé ; interruption : rien n'a été créé
  if (isAgent(artifact)) {
    if (artifact.message && artifact.message !== ARTIFACT_INTERRUPTED) return artifact.message;
    return kindOf(artifact) === 'agent_change' ? "La carte de modification de l'agent n'a pas pu être préparée." : "La carte « Nouvel agent » n'a pas pu être préparée.";
  }
  if (!isDocument(artifact)) return artifact.message || "Ce visuel n'a pas pu être construit.";
  if (!artifact.message || artifact.message === ARTIFACT_INTERRUPTED) return "Le document n'a pas pu être terminé.";
  return artifact.message;
}

function Pending({ artifact }) {
  if (isEmail(artifact)) {
    return (
      <div className="email-skeleton" role="status">
        <MessageIcon size={14} />
        <span>{kindOf(artifact) === 'email_cancel' ? "Préparation de la carte d'annulation…" : "Préparation du brouillon d'e-mail…"}</span>
      </div>
    );
  }
  // Même squelette que les cartes d'e-mail (style dans Visuals.css, chargé avec le fil : celui
  // de la carte d'agent n'arrive qu'avec son chunk) ; les essais peuvent durer jusqu'à 30 s
  if (isAgent(artifact)) {
    return (
      <div className="email-skeleton" role="status">
        <RadarIcon size={14} />
        <span>{kindOf(artifact) === 'agent_change' ? "Préparation de la modification de l'agent…" : "Préparation de l'agent…"}</span>
      </div>
    );
  }
  // Carte live : couche ou modification en préparation (la carte se met à jour dans le panneau)
  if (kindOf(artifact) === 'map' || kindOf(artifact) === 'map_patch' || visualFamily(artifact.kind, artifact.visual_type) === 'map') {
    return (
      <div className="lm-chatcard lm-chatcard--pending" role="status">
        <span className="lm-chatcard__icon" aria-hidden="true"><MapIcon size={16} /></span>
        <div className="lm-chatcard__body">
          <p className="lm-chatcard__kicker">Carte live</p>
          <p className="lm-chatcard__title">{kindOf(artifact) === 'map_patch' ? 'Mise à jour de la carte en direct…' : `Ajout à la carte en direct${artifact.title ? ` : ${artifact.title}` : '…'}`}</p>
        </div>
      </div>
    );
  }
  return isDocument(artifact)
    ? <DocumentSkeleton format={artifact.format} title={artifact.title} />
    : <VisualSkeleton visualType={artifact.visual_type} kind={artifact.kind} title={artifact.title} />;
}

/** Échec de chargement d'un chunk (réseau) : message neutre et nouvelle tentative. */
export class VisualBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <VisualNotice
        message="Le visuel n'a pas pu être chargé."
        onRetry={() => this.setState({ failed: false })}
      />
    );
  }
}

/** Visuel refusé ou interrompu : simple note, sans ton alarmant (la réponse reste lisible). */
export function VisualNotice({ title = null, message, onRetry = null }) {
  return (
    <div className="viz-notice" role="note">
      <InfoIcon size={14} />
      <span className="viz-notice__text">
        {title && <strong>{title} : </strong>}
        {message}
      </span>
      {onRetry && (
        <Button variant="outline" size="sm" icon={RefreshIcon} iconSize={12} className="viz-notice__retry" onClick={onRetry}>
          Réessayer
        </Button>
      )}
    </div>
  );
}

// La spécification est lue (normalizeSpec) dans le chunk de la carte : le bundle
// initial ne contient que le skeleton et cette note
// Visuel refusé puis reconstruit dans la même réponse (même titre) : le refus n'intéresse plus le lecteur
const titreDe = (artifact) => String(artifact.spec?.title ?? artifact.title ?? '').trim().toLowerCase();
export function sansEchecsCorriges(artifacts) {
  if (!artifacts?.length) return artifacts;
  return artifacts.filter((artifact, i) => {
    // Appel coupé puis rejoué par le serveur : rien à signaler au lecteur
    if (artifact.status === 'error' && /étape rejouée/.test(artifact.message ?? '')) return false;
    if (artifact.status !== 'error' || !titreDe(artifact)) return true;
    return !artifacts.slice(i + 1).some((suite) => suite.status !== 'error' && titreDe(suite) === titreDe(artifact));
  });
}

function ReadyVisual({ artifact, messageId }) {
  const Card = CARDS[visualFamily(artifact.spec?.kind ?? artifact.kind, artifact.spec?.visual_type ?? artifact.visual_type)] ?? VisualCard;
  return (
    <VisualBoundary>
      <Suspense fallback={<Pending artifact={artifact} />}>
        <Card rawSpec={artifact.spec} fallbackTitle={artifact.title} artifactId={artifact.id} messageId={messageId} />
      </Suspense>
    </VisualBoundary>
  );
}

/**
 * Visuels d'une réponse, SOUS le texte final, dans l'ordre de production :
 * skeleton typé (`artifact_pending`) → carte (`artifact`, en fondu) ou note (`artifact_error`).
 */
export const MessageVisuals = memo(function MessageVisuals({ artifacts, messageId, label = 'Visuels de la réponse' }) {
  const affiches = sansEchecsCorriges(artifacts);
  if (!affiches?.length) return null;
  return (
    <ul className="viz-list" aria-label={label}>
      {affiches.map((artifact) => (
        <li key={artifact.id} className="viz-slot">
          {artifact.status === 'pending' && <Pending artifact={artifact} />}
          {artifact.status === 'error' && <VisualNotice title={artifact.title} message={noticeMessage(artifact)} />}
          {artifact.status === 'ready' && <ReadyVisual artifact={artifact} messageId={messageId} />}
        </li>
      ))}
    </ul>
  );
});
